import type { Piece } from '@visualizador/shared';

/**
 * Peças ocultadas à mão ("Ocultar peça") — gravadas NESTE navegador.
 *
 * Por que gravar: quem monta o móvel esconde a frente para enxergar o interior
 * e fica ali um tempão, girando, medindo, recarregando a página sem querer.
 * Antes, qualquer recarregamento devolvia todas as peças e a pessoa tinha que
 * esconder tudo de novo. Agora o que estava escondido continua escondido.
 *
 * O que é gravado: o `caminho_pid` da peça — o identificador ESTÁVEL da
 * OCORRÊNCIA. O `id` não serve porque ele carrega um "#2"/"#3" posicional: se o
 * marceneiro republicar o móvel pelo plugin, a ocorrência que era "#2" pode
 * virar "#3" e o site esconderia a prateleira errada.
 *
 * MÓVEL PUBLICADO POR PLUGIN ANTIGO (antes do 0.9.3) não tem `caminho_pid`.
 * Nesse caso gravamos o `id` mesmo — é o único identificador que existe, e
 * naqueles móveis ele já era estável (o plugin antigo repetia o mesmo id em
 * todas as ocorrências, então o comportamento continua exatamente o de antes).
 *
 * Uma chave por MÓVEL na tela do móvel e uma por CLIENTE na tela de projeto:
 * esconder uma porta olhando um móvel sozinho não pode apagar a casa inteira
 * quando a pessoa abrir o projeto montado.
 *
 * TODA leitura/escrita vai dentro de try/catch: em aba anônima (e no Safari com
 * bloqueio de rastreamento) o localStorage LANÇA exceção, e esconder uma peça
 * não pode derrubar a tela por causa disso — sem armazenamento, volta a valer
 * só até recarregar, como antes.
 */

const PREFIXO = 'mania3d.ocultas';

/** Chave da tela de um móvel só. `arquivo` entra junto porque o mesmo movel_id pode existir em dois arquivos. */
export function chaveDoMovel(movelId: string, arquivo = ''): string {
  return `${PREFIXO}.movel:${arquivo}|${movelId}`;
}

/** Chave da tela de projeto (casa montada): o cliente é o `arquivo` do SketchUp. */
export function chaveDoCliente(arquivo: string): string {
  return `${PREFIXO}.cliente:${arquivo}`;
}

/**
 * Como esta peça é gravada. `caminho_pid` quando existe; senão o `id`
 * (móvel publicado por plugin antigo — ver o comentário do topo do arquivo).
 */
export function chaveDaPeca(p: Pick<Piece, 'id' | 'caminho_pid'>): string {
  return p.caminho_pid || p.id;
}

/** Lista gravada (chaves de peça). Devolve [] em qualquer problema — nunca lança. */
export function lerOcultas(chave: string): string[] {
  try {
    const bruto = localStorage.getItem(chave);
    if (!bruto) return [];
    const lista = JSON.parse(bruto) as unknown;
    // Defensivo: se o valor foi mexido a mão ou virou lixo, trata como vazio.
    return Array.isArray(lista) ? lista.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    // aba anônima / JSON estragado: o móvel abre com tudo visível
    return [];
  }
}

/** Grava a lista inteira (sem repetidos). Silencioso quando não há armazenamento. */
export function gravarOcultas(chave: string, lista: string[]): void {
  try {
    if (lista.length === 0) localStorage.removeItem(chave);
    else localStorage.setItem(chave, JSON.stringify([...new Set(lista)]));
  } catch {
    // sem armazenamento: vale só até recarregar a página
  }
}

/** Acrescenta uma peça à lista gravada e devolve a lista nova (para a tela contar). */
export function adicionarOculta(chave: string, chavePeca: string): string[] {
  if (!chavePeca) return lerOcultas(chave);
  const nova = [...new Set([...lerOcultas(chave), chavePeca])];
  gravarOcultas(chave, nova);
  return nova;
}

/**
 * Texto do botão "Exibir tudo". Quando há peça escondida ele diz quantas são:
 * sem esse aviso a pessoa abre o móvel, não acha a prateleira e conclui que ela
 * sumiu do projeto. Sem nenhuma escondida o texto continua exatamente
 * "Exibir tudo" (é assim que o botão é conhecido — e é o que os testes procuram).
 */
export function rotuloExibirTudo(quantas: number): string {
  if (quantas <= 0) return 'Exibir tudo';
  return `Exibir tudo (${quantas} ${quantas === 1 ? 'oculta' : 'ocultas'})`;
}

/** Esquece tudo o que estava escondido (botão "Exibir tudo"). */
export function limparOcultas(chave: string): void {
  gravarOcultas(chave, []);
}

/**
 * Traduz o que está gravado (caminho_pid) para os `id` que o viewer 3D entende,
 * usando as peças que vieram do banco. Peça gravada que não existe mais no móvel
 * (foi apagada no SketchUp) simplesmente não aparece aqui — some sozinha.
 */
export function idsParaEsconder(gravadas: string[], pieces: Pick<Piece, 'id' | 'caminho_pid'>[]): string[] {
  if (gravadas.length === 0) return [];
  const alvo = new Set(gravadas);
  return pieces.filter((p) => alvo.has(chaveDaPeca(p))).map((p) => p.id);
}

/**
 * Das chaves gravadas, as que ainda correspondem a uma peça deste móvel — é o
 * que o botão "Exibir tudo" conta. Se contasse a lista crua, uma peça apagada no
 * SketchUp deixaria o botão avisando de uma oculta que não existe mais.
 */
export function ocultasPresentes(gravadas: string[], pieces: Pick<Piece, 'id' | 'caminho_pid'>[]): string[] {
  if (gravadas.length === 0) return [];
  const alvo = new Set(gravadas);
  return pieces.map(chaveDaPeca).filter((k) => alvo.has(k));
}
